import { useAui, useAuiState } from "@assistant-ui/react";
import { ChevronDownIcon, SparklesIcon } from "@/design/icons";
import type { FC } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/design/ui/dropdown-menu";

/**
 * The selection actions as one pill that floats above the composer while
 * text is selected in Word. Each item sends one message; the selection
 * itself reaches the agent through the document snapshot on the run.
 */
export const IMPROVE_ACTIONS: { key: string; label: string; prompt: string }[] = [
  { key: "polish", label: "Polish", prompt: "Suggest changes to polish the selected text." },
  {
    key: "shorten",
    label: "Shorten",
    prompt: "Suggest changes to shorten the selected text without losing any point or figure.",
  },
  { key: "expand", label: "Expand", prompt: "Suggest changes to expand the selected text." },
  {
    key: "tone",
    label: "Match the bid's tone",
    prompt: "Rewrite the selected text in the bid's tone of voice, keeping every point and figure.",
  },
  {
    key: "evidence",
    label: "Add evidence",
    prompt:
      "Find claims in the selected text with no evidence and add specific evidence from our content library after each, as tracked changes.",
  },
  {
    key: "comment",
    label: "Comment on this",
    prompt: "Review the selected text against the evaluation criteria and leave your findings as comments on it.",
  },
];

export const ImprovePill: FC<{ selectedText: string | null; onAddToQuestion?: () => void }> = ({
  selectedText,
  onAddToQuestion,
}) => {
  const aui = useAui();
  const isRunning = useAuiState((s) => s.thread.isRunning);
  const text = selectedText?.trim();
  if (!text || isRunning) return null;
  const words = (text.match(/\S+/g) ?? []).length;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="pointer-events-auto inline-flex h-[30px] items-center gap-1.5 rounded-full bg-fg pl-2.5 pr-3 text-sm font-semibold text-fg-inverse shadow-pop transition-transform duration-fast hover:scale-[1.02] active:scale-100 animate-slide-up"
        >
          <SparklesIcon className="size-3.5 text-orange-light" />
          Selection
          <span className="text-fg-4">· {words} words</span>
          <ChevronDownIcon className="size-3 text-fg-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="center" sideOffset={6} className="w-56">
        {onAddToQuestion && (
          <>
            <DropdownMenuItem onSelect={onAddToQuestion}>
              <span className="flex-1">Add to question…</span>
              <span className="text-xs text-fg-3">{words} words</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="py-1 text-xs font-semibold text-fg-3">Improve</DropdownMenuLabel>
          </>
        )}
        {IMPROVE_ACTIONS.map((action) => (
          <DropdownMenuItem
            key={action.key}
            onSelect={() => aui.thread().append({ role: "user", content: [{ type: "text", text: action.prompt }] })}
          >
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
